import { Mapper, FeedItem, FirestoreEntity, AnyFeedItemEntityVersion } from '@uploadcam/common';
import {
  CollectionReference,
  QueryConstraint,
  getDocs,
  limit,
  query,
  startAfter,
} from 'firebase/firestore';
import { createFeedQuery } from '../helpers/create-feed-query';

export interface FeedFilter {
  uploadedBy?: string;
  albumId?: string;
  folder?: {
    id: string;
    name: string;
  };
  afterDate?: Date;
  beforeDate?: Date;
  geoBBox?: google.maps.LatLngBoundsLiteral;
  tags?: readonly string[];
}

interface GetTeamFeedResult {
  page: FeedItem[];
  isLastPage: boolean;
}

export class GetTeamFeedInteractor {
  constructor(
    private readonly collection: CollectionReference<AnyFeedItemEntityVersion>,
    private readonly mapper: Mapper<unknown, FeedItem>,
  ) {}

  /**
   * @param teamId
   * @param pageSize
   * @param filter
   * @param lastCreatedAt Value to use as "offset", if provided it'll retrieve the `FeedItems` coming after the entry with the given `createdAt`
   */
  public async execute(
    teamId: string,
    pageSize: number,
    filter?: FeedFilter,
    lastFeedItem?: FeedItem,
  ): Promise<GetTeamFeedResult> {
    let feedQuery: QueryConstraint[] = [
      ...createFeedQuery(teamId, filter),
      limit(pageSize + 1), // Add one extra document to determine if there are more pages
    ];

    if (lastFeedItem) {
      feedQuery.push(startAfter(lastFeedItem.createdAt));
    }

    try {
      const result = await getDocs(query(this.collection, ...feedQuery));

      return {
        // Discard the extra document used to determine if there are more pages
        page: result.docs
          .slice(0, pageSize)
          .map((doc) => this.mapper.map(FirestoreEntity.from(doc))),
        isLastPage: result.docs.length <= pageSize,
      };
    } catch (error) {
      console.error('An error occurred while getting documents:', error);
      // Handle the error as needed, for example, you might want to re-throw it or return a default value
      throw error;
    }
  }
}
